import { styled, Switch, SwitchProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { StateNames, States } from 'src/Screens/Configurator';
import { checkMinMax } from 'src/utils/3d.utils';
import { createBoxes } from 'src/utils/box.utils';
import {
  changeCustomElementStatus as changeBoxStatus,
  isCustomElementUse as isBoxUse,
} from 'src/utils/common.utils';

type CustomBoxesProps = {
  pieces: any[];
  setCustomBoxes: (customBoxes: any[]) => void;
  customPlatings: any[];
  customBoxes: any[];
  setValue: <T extends StateNames>(stateName: T, value: States[T]) => void;
  getValue: <T extends StateNames>(stateName: T) => States[T];
};

export const CustomBoxes = ({
  pieces,
  setCustomBoxes,
  customBoxes,
  setValue,
  getValue,
  customPlatings,
}: CustomBoxesProps) => {
  const [orientation, setOrientation] = useState<'z' | 'x'>();
  const [width, setWidth] = useState<string>();
  const [length, setLength] = useState<string>();
  const [depth, setDepth] = useState<string>();
  const [error, setError] = useState<string>();
  const [hasSlope, setHasSlope] = useState<boolean>();

  const [height, setHeight] = useState<{ front: string; back: string }>({
    front: '10',
    back: '',
  });
  const [mode, setMode] = useState<'closoir' | 'double closoir' | 'tete'>();

  useEffect(() => {
    if (customBoxes.length > 0) {
      if (hasSlope === undefined)
        setHasSlope(customBoxes[0]?.shapeDetails.hasSlope);
      if (orientation === undefined)
        setOrientation(customBoxes[0]?.shapeDetails.orientation);
      if (mode === undefined) setMode(customBoxes[0]?.shapeDetails.mode);

      setWidth(
        String(
          Math.round(
            Number.parseFloat(customBoxes[0]?.shapeDetails.monumentWidth) * 100,
          ),
        ),
      );
      setLength(
        String(
          Math.round(
            Number.parseFloat(customBoxes[0]?.shapeDetails.monumentLength) *
              100,
          ),
        ),
      );
      setDepth(
        String(
          Math.round(
            Number.parseFloat(customBoxes[0]?.shapeDetails.depth) * 100,
          ),
        ),
      );

      setHeight({
        front:
          customBoxes[0]?.shapeDetails.monumentFront === undefined
            ? String(Number.parseFloat(height.front) / 100)
            : String(
                Math.round(
                  Number.parseFloat(
                    customBoxes[0]?.shapeDetails.monumentFront,
                  ) * 100,
                ),
              ),
        back:
          customBoxes[0]?.shapeDetails.monumentBack === undefined
            ? ''
            : String(
                Math.round(
                  Number.parseFloat(customBoxes[0]?.shapeDetails.monumentBack) *
                    100,
                ),
              ),
      });
    }
  }, [customBoxes]);

  const updatePiece = (forceInformation?: {
    orientation?: 'x' | 'z';
    mode?: 'closoir' | 'double closoir' | 'tete';
    customBoxes?: any[];
    height?: { front: string; back: string };
  }) => {
    const heightToTake = forceInformation?.height ?? height;
    setError(undefined);
    createBoxes(
      pieces,
      customPlatings,
      forceInformation?.customBoxes ?? customBoxes,
      setCustomBoxes,
      {
        length: checkMinMax(
          Number.parseInt(length ?? '0', 10) / 100,
          customBoxes[0]?.granit,
          setError,
        ),
        width: checkMinMax(
          Number.parseInt(width ?? '0', 10) / 100,
          customBoxes[0]?.granit,
          setError,
        ),
        heightFront: checkMinMax(
          Number.parseInt(heightToTake.front ?? '0', 10) / 100,
          customBoxes[0]?.granit,
          setError,
        ),
        heightBack:
          heightToTake.back === ''
            ? undefined
            : checkMinMax(
                Number.parseInt(heightToTake.back, 10) / 100,
                customBoxes[0]?.granit,
                setError,
              ),
        orientation: forceInformation?.orientation ?? orientation,
        mode: forceInformation?.mode ?? mode,
        hasSlope,
        depth: Number.parseInt(depth ?? '0', 10) / 100,
      },
    );
  };

  return (
    <div className='h-full w-full'>
      {getValue('hasAdvancedMode') ? (
        <div className='bg-[#D32F2F70] px-3 py-1.5 rounded-xl mb-3'>
          <p className='text-xs'>Attention, le mode avancé est activé.</p>
          <p className='text-xs'>
            Les assemblages de pièces ne sont plus pris en compte. Vous
            modifierez et déplacerez chaque pièce indépendamment des autres.
          </p>
        </div>
      ) : null}

      <div className='flex flex-row justify-between'>
        <div className='flex flex-row'>
          <span className='text-sm text-[#344054] cursor-pointer z-[2]'>
            Dimension
          </span>
          <span className='text-sm text-[#344054] ml-1 mb-2.5 font-bold'>
            Box
          </span>
        </div>
        <div className='flex flex-row items-center gap-1.5 ml-0'>
          <span className='text-xs text-[#344054] font-normal'>
            Mode avancé
          </span>
          <IOSSwitch
            checked={getValue('hasAdvancedMode')}
            onChange={() =>
              setValue('hasAdvancedMode', !getValue('hasAdvancedMode'))
            }
          />
        </div>
      </div>

      {error ? (
        <p className="font-['Open_Sans'] font-semibold pt-4 text-red-600 text-sm">
          {error}
        </p>
      ) : null}

      <div className='flex flex-row'>
        <div className='flex flex-row w-[60%]'>
          <div className='flex flex-col'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Longueur totale
            </p>
            <div className='flex flex-row'>
              <input
                className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                value={length}
                onChange={(e) => setLength(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <div className='flex justify-center items-center m-2.5 text-[#667085]'>
                x
              </div>
            </div>
          </div>
          <div className='flex flex-col'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Largeur totale
            </p>
            <div className='flex flex-row'>
              <input
                className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              <div className='flex justify-center items-center m-2.5 text-[#667085]' />
            </div>
          </div>
        </div>
        <div className='flex flex-col w-[40%]'>
          <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
            Epaisseur
          </p>
          <input
            className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
            value={depth}
            onChange={(e) => setDepth(e.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') updatePiece();
            }}
            onBlur={() => {
              updatePiece();
            }}
          />
        </div>
      </div>
      <div className='flex flex-row'>
        <div className='flex flex-row w-[60%]'>
          <div className='flex flex-col'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Hauteur{' '}
              {hasSlope ? (orientation === 'z' ? 'Avant' : 'Gauche') : ''}
            </p>
            <div className='flex flex-row'>
              <input
                className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                value={height.front}
                onChange={(e) =>
                  setHeight({ ...height, front: e.target.value })
                }
                onKeyDown={(event) => {
                  if (event.key === 'Enter') updatePiece();
                }}
                onBlur={() => {
                  updatePiece();
                }}
              />
              {hasSlope ? (
                <div className='flex justify-center items-center m-2.5 text-[#667085]'>
                  /
                </div>
              ) : null}
            </div>
          </div>

          {hasSlope ? (
            <div className='flex flex-col justify-end'>
              <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
                Hauteur{' '}
                {hasSlope ? (orientation === 'z' ? 'Arrière' : 'Droite') : ''}
              </p>

              <div className='flex flex-row'>
                <input
                  className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135] appearance-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[#BC9A67]'
                  value={height.back}
                  onChange={(e) =>
                    setHeight({ ...height, back: e.target.value })
                  }
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') updatePiece();
                  }}
                  onBlur={() => {
                    updatePiece();
                  }}
                />
                <div className='flex justify-center items-center m-2.5 text-[#667085]' />
              </div>
            </div>
          ) : null}
        </div>
        {hasSlope ? (
          <div className='flex flex-col w-[40%]'>
            <p className="font-['Open_Sans'] font-semibold text-xs leading-4 mt-[18px] mb-2 text-black/50">
              Sens de la pente
            </p>
            <select
              className='w-full py-2.5 px-1.5 rounded-lg border border-[#C2C9D1] text-[#273135]'
              name='sens'
              id='sens'
              onChange={(e) => {
                setOrientation(e.target.value as 'x' | 'z');
                updatePiece({ orientation: e.target.value as 'x' | 'z' });
              }}
              disabled={!hasSlope}
              value={orientation}
            >
              <option selected={orientation === 'z'} value='z'>
                Avant/Arrière
              </option>
              <option selected={orientation === 'x'} value='x'>
                Gauche/Droit
              </option>
            </select>
          </div>
        ) : null}
      </div>

      <div className='flex flex-row items-center mt-3'>
        <input
          type='checkbox'
          className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
          checked={hasSlope}
          onChange={() => {
            setHeight({ ...height, back: '' });
            setHasSlope(!hasSlope);

            updatePiece({ height: { front: height.front, back: '' } });
          }}
        />
        <span className='text-sm text-[#273135]'>Rajouter une pente</span>
      </div>
      <div className='flex flex-row'>
        <div className='relative mt-[42px] w-[188px] h-[193px] min-h-[193px] max-h-[193px] min-w-[188px] max-w-[188px]'>
          <div className='flex flex-row h-full'>
            <img
              src='/icons/plating-left.svg'
              className='cursor-pointer w-[82px] h-[105px] min-w-[82px] min-h-[105px] max-w-[82px] max-h-[105px]'
              style={{
                filter: isBoxUse(customBoxes, 'left')
                  ? ''
                  : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
              }}
              onClick={() => {
                changeBoxStatus('left', customBoxes, setCustomBoxes);
                updatePiece({
                  customBoxes: customBoxes.map((el) => {
                    if (el.key === 'left')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
            <div className='flex flex-col justify-between'>
              <img
                src='/icons/plating-back.svg'
                className='cursor-pointer ml-[5px] mt-0.5 z-[2] w-[80px] h-[29px] min-w-[80px] min-h-[29px] max-w-[80px] max-h-[29px]'
                style={{
                  filter: isBoxUse(customBoxes, 'back')
                    ? ''
                    : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
                }}
                onClick={() => {
                  changeBoxStatus('back', customBoxes, setCustomBoxes);
                  updatePiece({
                    customBoxes: customBoxes.map((el) => {
                      if (el.key === 'back')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
              <img
                src='/icons/plating-front.svg'
                className='cursor-pointer ml-[-70px] mb-[50px] w-[117px] h-[53px] min-w-[117px] min-h-[53px] max-w-[117px] max-h-[53px]'
                style={{
                  filter: isBoxUse(customBoxes, 'front')
                    ? ''
                    : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
                }}
                onClick={() => {
                  changeBoxStatus('front', customBoxes, setCustomBoxes);
                  updatePiece({
                    customBoxes: customBoxes.map((el) => {
                      if (el.key === 'front')
                        return {
                          ...el,
                          isActive: !el.isActive,
                        };
                      return el;
                    }),
                  });
                }}
              />
            </div>
            <img
              src='/icons/plating-right.svg'
              className='cursor-pointer ml-[-30px] mt-2.5 w-[39px] h-[130px] min-w-[39px] min-h-[130px] max-w-[39px] max-h-[130px]'
              style={{
                filter: isBoxUse(customBoxes, 'right')
                  ? ''
                  : 'invert(41%) sepia(2%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(90%)',
              }}
              onClick={() => {
                changeBoxStatus('right', customBoxes, setCustomBoxes);
                updatePiece({
                  customBoxes: customBoxes.map((el) => {
                    if (el.key === 'right')
                      return {
                        ...el,
                        isActive: !el.isActive,
                      };
                    return el;
                  }),
                });
              }}
            />
          </div>
          <p className='text-sm text-center italic text-[#667085]'>
            Cliquez sur une box pour la retirer ou l&apos;ajouter.
          </p>
        </div>
        <div className='flex flex-col w-full justify-center p-5'>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'tete'}
              onChange={() => {
                setMode('tete');
                updatePiece({ mode: 'tete' });
              }}
            />
            <span className='text-sm text-[#273135]'>
              Tête avant / tête arrière
            </span>
          </div>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'closoir'}
              onChange={() => {
                setMode('closoir');
                updatePiece({ mode: 'closoir' });
              }}
            />
            <span className='text-sm text-[#273135]'>Closoir arrière</span>
          </div>
          <div className='flex flex-row items-center'>
            <input
              type='checkbox'
              className='w-[1.3em] h-[1.3em] bg-white rounded-full align-middle border border-[#ddd] appearance-none outline-none cursor-pointer p-2 mr-2 checked:bg-[#BC9A67] checked:border-[#BC9A67]'
              checked={mode === 'double closoir'}
              onChange={() => {
                setMode('double closoir');
                updatePiece({ mode: 'double closoir' });
              }}
            />
            <span className='text-sm text-[#273135]'>Double closoir</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#BC9A67' : '#BC9A67',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#BC9A67',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));
